import React, { useRef } from 'react';

import type { FileUIPart } from 'ai';

import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import MicNoneOutlinedIcon from '@mui/icons-material/MicNoneOutlined';
import StopRoundedIcon from '@mui/icons-material/StopRounded';
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight';
import {
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  IconButton,
  InputBase,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import RecordingWaveform from '~/components/Common/RecordingWaveform';
import { LensMark } from '~/components/brand';

import useAudioDictation, { DICTATION_SUPPORTED } from '~/hooks/useAudioDictation';

import { RICHARD_MICROCOPY } from '../chat/microcopy';
import { DUR_STATE, EASE_STATE, ICON_PRESS_SX } from '../chat/uiTokens';
import { COMPOSER_IMAGE_ACCEPT, MAX_COMPOSER_IMAGES } from '../imageAttachments';

import ImageThumbnails from './ImageThumbnails';
import { paneTokens } from './paneTokens';

/** What the composer is scoped to: a Lens card (removable) or a dataset (fixed). */
export type ComposerScope = {
  label: string;
  /** Shown as the Lens ring-dot when the scope is a Lens card. */
  lens?: boolean;
  /** Present when the person can drop the scope and ask about the whole Lens. */
  onClear?: () => void;
};

interface UiStreamComposerProps {
  variant?: 'page' | 'pane';
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  onStop: () => void;
  onDictated: (transcript: string) => void;
  attachments: ReadonlyArray<FileUIPart>;
  attachmentsReading: boolean;
  onAttach: (files: ReadonlyArray<File>) => void;
  onRemoveAttachment: (index: number) => void;
  working: boolean;
  stopAvailable?: boolean;
  canSteer: boolean;
  hasThread: boolean;
  inputRef: React.RefObject<HTMLTextAreaElement | null>;
  idlePlaceholder?: string;
  sendBlocked?: boolean;
  scope?: ComposerScope;
  /** Optional prompts stay in the toolbar so they do not add a separate row. */
  examples?: React.ReactNode;
}

const MAX_ROWS = 8;
const TOOL_BUTTON_SX = { width: 28, height: 28, color: 'text.secondary' } as const;
const SEND_SIZE = 28;

export function composerPlaceholder(working: boolean, hasThread: boolean): string {
  if (working) {
    return RICHARD_MICROCOPY.steerPlaceholder;
  }
  return hasThread ? 'Ask a follow-up' : 'Ask about bookings, revenue, customers or offers';
}

function ScopePill({ scope }: { scope: ComposerScope }) {
  const theme = useTheme();
  const tokens = paneTokens(theme);
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.75}
      data-testid="ui-stream-composer-scope"
      sx={{
        alignSelf: 'flex-start',
        maxWidth: '100%',
        height: 26,
        pl: 1,
        pr: scope.onClear ? 0.5 : 1,
        border: `1px solid ${tokens.hairline}`,
        borderRadius: '8px',
        color: tokens.accent,
      }}
    >
      {scope.lens ? (
        <Box sx={{ display: 'flex', flex: 'none' }}>
          <LensMark size={13} inverted={theme.palette.mode === 'dark'} />
        </Box>
      ) : null}
      <Typography
        noWrap
        sx={{ fontSize: 12.5, lineHeight: 1, fontWeight: 500, color: 'inherit', minWidth: 0 }}
        title={scope.label}
      >
        {scope.label}
      </Typography>
      {scope.onClear ? (
        <Tooltip title="Ask about the whole Lens">
          <ButtonBase
            aria-label={`Remove ${scope.label} from the question`}
            onClick={scope.onClear}
            sx={{
              width: 18,
              height: 18,
              borderRadius: '50%',
              color: 'inherit',
              display: 'flex',
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            <CloseIcon sx={{ fontSize: 12, display: 'block' }} />
          </ButtonBase>
        </Tooltip>
      ) : null}
    </Stack>
  );
}

/**
 * The main chat uses a white bordered box; the Lens pane uses a hairline rule.
 * Both hold the question above attach, dictate and send, with the scope pill outside.
 */
export default function UiStreamComposer({
  variant = 'page',
  value,
  onChange,
  onSubmit,
  onStop,
  onDictated,
  attachments,
  attachmentsReading,
  onAttach,
  onRemoveAttachment,
  working,
  stopAvailable = working,
  canSteer,
  hasThread,
  inputRef,
  idlePlaceholder,
  sendBlocked = false,
  scope,
  examples,
}: UiStreamComposerProps) {
  const theme = useTheme();
  const tokens = paneTokens(theme);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const dictation = useAudioDictation(onDictated);
  const recording = dictation.state === 'recording';
  const transcribing = dictation.state === 'transcribing';
  const hasText = value.trim().length > 0;
  const steering = working && hasText;
  const canSend = hasText && !recording && !transcribing && !sendBlocked && (working ? canSteer : !attachmentsReading);
  const canAttach = !working && attachments.length < MAX_COMPOSER_IMAGES;

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Escape' && stopAvailable) {
      event.preventDefault();
      onStop();
      return;
    }
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      if (canSend) {
        onSubmit();
      }
    }
  };

  const handlePaste = (event: React.ClipboardEvent) => {
    const files = Array.from(event.clipboardData.items)
      .filter((item) => item.type.startsWith('image/'))
      .flatMap((item) => item.getAsFile() ?? []);
    if (files.length === 0 || working) {
      return;
    }
    event.preventDefault();
    onAttach(files);
  };

  const handleDrop = (event: React.DragEvent) => {
    if (!event.dataTransfer.files.length) {
      return;
    }
    event.preventDefault();
    if (!working) {
      onAttach(Array.from(event.dataTransfer.files));
    }
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onAttach(Array.from(event.target.files ?? []));
    event.target.value = '';
  };

  return (
    <Stack spacing={1.25} sx={{ minWidth: 0 }}>
      {scope ? <ScopePill scope={scope} /> : null}
      <Box
        data-testid="ui-stream-composer"
        data-working={working || undefined}
        onDrop={handleDrop}
        onDragOver={(event) => event.preventDefault()}
        sx={{
          ...(variant === 'page'
            ? {
                px: 1.5,
                py: 1.25,
                bgcolor: 'background.paper',
                border: `1px solid ${tokens.composerRule}`,
                borderRadius: '8px',
                '&:focus-within': { borderColor: tokens.accent },
                ...(working ? { borderColor: tokens.accent } : {}),
              }
            : {
                pt: 1.25,
                borderTop: `1px solid ${tokens.composerRule}`,
                '&:focus-within': {
                  borderTopColor: tokens.accent,
                  boxShadow: `inset 0 1px 0 ${tokens.accent}`,
                },
                ...(working ? { borderTopColor: tokens.accent, boxShadow: `inset 0 1px 0 ${tokens.accent}` } : {}),
              }),
          transition: `border-color ${DUR_STATE}ms ${EASE_STATE}, box-shadow ${DUR_STATE}ms ${EASE_STATE}`,
          '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
        }}
      >
        <ImageThumbnails images={attachments} onRemove={onRemoveAttachment} />
        {recording && dictation.stream ? (
          <Box sx={{ minHeight: 30, display: 'flex', alignItems: 'center' }}>
            <RecordingWaveform stream={dictation.stream} />
          </Box>
        ) : (
          <InputBase
            inputRef={inputRef}
            value={value}
            onChange={(event) => onChange(event.target.value)}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            placeholder={!working && idlePlaceholder ? idlePlaceholder : composerPlaceholder(working, hasThread)}
            multiline
            maxRows={MAX_ROWS}
            fullWidth
            autoFocus
            inputProps={{ 'aria-label': working ? 'Steer askRichard' : 'Ask askRichard' }}
            sx={{ fontSize: 15, lineHeight: 1.5, py: 0 }}
          />
        )}
        {dictation.error ? (
          <Typography variant="caption" color="error" component="p">
            {dictation.error}
          </Typography>
        ) : null}
        <input
          ref={fileInputRef}
          type="file"
          accept={COMPOSER_IMAGE_ACCEPT}
          multiple
          hidden
          onChange={handleFileInputChange}
        />
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.75, ml: -0.5 }}>
          <Tooltip title="Attach images">
            <span>
              <IconButton
                size="small"
                aria-label="Attach images"
                disabled={!canAttach}
                onClick={() => fileInputRef.current?.click()}
                sx={TOOL_BUTTON_SX}
              >
                <AttachFileIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </span>
          </Tooltip>
          {DICTATION_SUPPORTED ? (
            <Tooltip title={recording ? 'Stop recording' : 'Dictate'}>
              <span>
                <IconButton
                  size="small"
                  aria-label={recording ? 'Stop recording' : 'Dictate'}
                  aria-pressed={recording}
                  disabled={transcribing}
                  onClick={dictation.toggle}
                  sx={{ ...TOOL_BUTTON_SX, ...(recording ? { color: 'error.main' } : {}) }}
                >
                  {transcribing ? (
                    <CircularProgress size={14} />
                  ) : recording ? (
                    <StopRoundedIcon sx={{ fontSize: 18 }} />
                  ) : (
                    <MicNoneOutlinedIcon sx={{ fontSize: 18 }} />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          ) : null}
          {examples}
          <Box sx={{ flex: 1 }} />
          {stopAvailable ? (
            <Button
              size="small"
              variant="text"
              startIcon={<StopRoundedIcon />}
              onClick={onStop}
              aria-label="Stop askRichard"
              sx={{ color: tokens.accent, minHeight: SEND_SIZE, px: 1, mr: 0.5 }}
            >
              Stop
            </Button>
          ) : null}
          <Tooltip title={steering ? 'Steer the answer in progress' : 'Send'}>
            <span>
              <IconButton
                onClick={onSubmit}
                disabled={!canSend}
                aria-label={steering ? 'Steer' : 'Send'}
                data-ready={hasText || undefined}
                sx={{
                  width: SEND_SIZE,
                  height: SEND_SIZE,
                  borderRadius: '8px',
                  bgcolor: hasText ? tokens.accent : 'action.disabledBackground',
                  color: hasText ? tokens.accentContrast : 'action.disabled',
                  transition: `background-color ${DUR_STATE}ms ${EASE_STATE}, color ${DUR_STATE}ms ${EASE_STATE}, transform ${DUR_STATE}ms ${EASE_STATE}`,
                  '&:hover': {
                    bgcolor: hasText ? tokens.accent : 'action.disabledBackground',
                    filter: 'brightness(1.08)',
                  },
                  '&.Mui-disabled': { color: 'action.disabled', bgcolor: 'action.disabledBackground' },
                  ...ICON_PRESS_SX,
                  '@media (prefers-reduced-motion: reduce)': { transition: 'none', '&:active': { transform: 'none' } },
                }}
              >
                {steering ? (
                  <SubdirectoryArrowRightIcon sx={{ fontSize: 16 }} />
                ) : (
                  <ArrowUpwardRoundedIcon sx={{ fontSize: 16 }} />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Box>
    </Stack>
  );
}
